import React, { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import c from 'classnames';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import Modal, { MODAL_FOOTER_BUTTON_SIZE } from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import InfoIcon from '@wix/da-ds/pkg/Icons/16x16/Info';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import { PapiDeviation } from '@wix/da-papi-types';
import DurationSlider from './_partials/DurationSlider';
import TagSelection from './_partials/TagSelection';
import { useBoostCoreModal } from './_partials/hooks';
import s from './BoostCoreModal.scss';

interface Props {
  deviation: PapiDeviation;
  onSuccess: () => void;
  onRequestClose: () => void;
}

const BoostCoreModal: React.FC<Props> = ({ deviation, onSuccess, onRequestClose }) => {
  const { t } = useTranslation();
  const { freeBoostsAllowed: freeBoostsTotal, pointsBalance } =
    deviation?.extended?.boostRestrictions || {};
  const isMobile = useContext(MobileContext);

  // Use the custom hook to manage all modal logic
  const {
    selectedDuration,
    selectedTags,
    isSubmitting,
    availableTags,
    pricingInfo,
    isLoading,
    error,
    setSelectedDuration,
    setSelectedTags,
    handleSubmit,
    handleClose,
    canSubmit,
    ctaText,
  } = useBoostCoreModal({
    deviation,
    onSuccess,
    onRequestClose,
  });
  const { pointsNeeded, freeBoostsNeeded: freeBoostsUsed } = pricingInfo;

  const renderMessage = () => (
    <div className={s['message']}>
      <div className={s['message-content']}>
        {/* prettier-ignore */}
        <Trans i18nKey="boostDeviation.modal.core.message">
          Boosts last 2 days. Stack them for longer exposure.
        </Trans>
        <SimpleTooltip
          text={
            <>
              {t('boostDeviation.modal.tooltipText')}{' '}
              <a
                className="link"
                target="_blank"
                href={t('boostDeviation.learnMoreUrl')}
                rel="noreferrer"
              >
                {t('boostDeviation.modal.learnMore')}
              </a>
            </>
          }
          width={300}
        >
          <button
            className={s['info-button']}
            aria-label={t('boostDeviation.modal.learnMoreAriaLabel')}
          >
            <InfoIcon size={IconSize.SMALLER} />
          </button>
        </SimpleTooltip>
      </div>
    </div>
  );

  const renderDurationSection = () => (
    <div className={s['duration-section']}>
      <h4 className={s['section-title']}>{t('boostDeviation.modal.chooseDuration')}</h4>
      <DurationSlider
        value={selectedDuration}
        onChange={setSelectedDuration}
        className={s['duration-slider']}
      />
    </div>
  );

  const renderTagSettings = () => (
    <TagSelection
      tags={availableTags}
      selectedTags={selectedTags}
      onTagSelectionChange={setSelectedTags}
      className={s['tag-settings']}
    />
  );

  const renderPointsBalance = () =>
    !!pointsBalance && (
      <span className={s['points-balance']}>
        {/* prettier-ignore */}
        <Trans i18nKey="boostDeviation.modal.core.pointsBalance" count={pointsBalance} values={{ pointsBalance }}>
          Balance: 
          <strong>pointsBalance</strong> 
          Points.
        </Trans>
      </span>
    );

  const renderPaymentInfo = () => {
    if (freeBoostsUsed === 0) {
      return (
        <div className={s['payment-info']}>
          <div className={s['payment-summary']}>
            <div className={s['payment-line']}>
              {/* prettier-ignore */}
              <Trans i18nKey="boostDeviation.modal.noFreeBoosts" values={{ freeBoostsUsed, freeBoostsTotal }}>
              <strong>{freeBoostsUsed}</strong> of <strong>{freeBoostsTotal}</strong> free boosts remain.
            </Trans>
              {pointsNeeded > 0 && (
                <>
                  {' '}
                  {/* prettier-ignore */}
                  <Trans i18nKey="boostDeviation.modal.payPoints" values={{ pointsNeeded }}>
                  Pay <strong>{pointsNeeded}</strong> points.
                  </Trans>
                  <br />
                  {renderPointsBalance()}
                </>
              )}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className={s['payment-info']}>
        <div className={s['payment-summary']}>
          <div className={s['payment-line']}>
            {!pointsNeeded && (
              <>
                {/* prettier-ignore */}
                <Trans i18nKey="boostDeviation.modal.useFreeBoosts" values={{ freeBoostsUsed, freeBoostsTotal }}>
                  Use 
                  <strong>{freeBoostsUsed}</strong>
                  of 
                  <strong>{freeBoostsTotal}</strong>
                  free boosts remain.
                </Trans>
              </>
            )}
            {!!pointsNeeded && (
              <>
                {/* prettier-ignore */}
                <Trans i18nKey="boostDeviation.modal.useFreeBoostsPlusPayPoints" values={{ freeBoostsUsed, freeBoostsTotal, pointsNeeded }}>
                  Use 
                  <strong>{freeBoostsUsed}</strong>
                  of 
                  <strong>{freeBoostsTotal}</strong>
                  free boosts remain + pay 
                  <strong>{pointsNeeded}</strong>
                  points.
                </Trans>
                <br />
                {renderPointsBalance()}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Show loading state while initializing boost data
  if (isLoading) {
    return (
      <Modal
        isOpen
        onRequestClose={onRequestClose}
        heading={t('boostDeviation.modal.core.title')}
        contentClassName={s['content']}
        showHeaderBorder
      >
        <div className={s['loading-container']}>
          <LoadingIndicator />
          <p>{t('boostDeviation.modal.loading')}</p>
        </div>
      </Modal>
    );
  }

  // Show error state if initialization failed
  if (error) {
    return (
      <Modal
        isOpen
        onRequestClose={onRequestClose}
        heading={t('boostDeviation.modal.core.title')}
        contentClassName={s['content']}
        showHeaderBorder
        footerRight={[
          <TextButton
            key="close"
            variant="main"
            onClick={handleClose}
            size={MODAL_FOOTER_BUTTON_SIZE}
          >
            {t('common.close')}
          </TextButton>,
        ]}
      >
        <div className={s['error-container']}>
          <p>{t('boostDeviation.modal.error')}</p>
          <p className={s['error-message']}>{error}</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen
      onRequestClose={onRequestClose}
      heading={t('boostDeviation.modal.core.title')}
      contentClassName={s['content']}
      showHeaderBorder
      subheading={renderMessage()}
      isFullscreen={isMobile}
      useFixedMobileHeight={isMobile}
      footerRight={
        isMobile
          ? undefined
          : [
              <TextButton
                key="cancel"
                variant="main"
                onClick={handleClose}
                disabled={isSubmitting}
                size={MODAL_FOOTER_BUTTON_SIZE}
              >
                {t('boostDeviation.modal.button.boostLater')}
              </TextButton>,
              <BrandedButton
                key="confirm"
                variant="brand-green"
                loading={isSubmitting}
                size={MODAL_FOOTER_BUTTON_SIZE}
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                {ctaText}
              </BrandedButton>,
            ]
      }
      footer={
        isMobile && (
          <div className={s['footer']}>
            <TextButton
              key="cancel"
              variant="main"
              onClick={handleClose}
              disabled={isSubmitting}
              size={MODAL_FOOTER_BUTTON_SIZE}
            >
              {t('boostDeviation.modal.button.boostLater')}
            </TextButton>
            <BrandedButton
              key="confirm"
              variant="brand-green"
              loading={isSubmitting}
              size={MODAL_FOOTER_BUTTON_SIZE}
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {ctaText}
            </BrandedButton>
          </div>
        )
      }
    >
      <div className={s['modal-content']}>
        <div className={s['illustration-container']}>
          <div className={c(s['boost-illustration'], s[`boost-duration-${selectedDuration}d`])} />
        </div>
        {renderDurationSection()}
        <div className={s['divider']} />
        {renderTagSettings()}
        <div className={s['divider']} />
        {renderPaymentInfo()}
      </div>
    </Modal>
  );
};

BoostCoreModal.displayName = 'BoostCoreModal';

export default BoostCoreModal;
